import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import i18n from 'i18next';
import { SUPPORTED_LNGS } from '../supportedLngs.js';
import { setVisitorLang } from '../visitorContext.js';
import { trackEvent } from '../analytics.js';

import './LanguageSwitcher.css';

/** Native labels for the switcher UI (SEO URLs still use ISO codes: es, zh, …). */
export const LANGUAGE_LABELS = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  ru: 'Русский',
  it: 'Italiano',
  es: 'Español',
  he: 'עברית',
  zh: '简体中文',
  ja: '日本語',
  ar: 'العربية',
};

/**
 * Builds the path when switching UI language while keeping airport / since / event segments.
 */
export function pathForLanguage(nextLng, params) {
  if (!nextLng || !SUPPORTED_LNGS.includes(nextLng)) {
    return '/';
  }
  const { source, starting, event } = params || {};
  if (source) {
    let p = `/${nextLng}/from/${encodeURIComponent(source)}`;
    if (starting) {
      p += `/since/${encodeURIComponent(starting)}`;
    } else if (event != null && event !== '') {
      p += `/event/${encodeURIComponent(event)}`;
    }
    return p;
  }
  return `/${nextLng}`;
}

export default function LanguageSwitcher() {
  const params = useParams();
  const navigate = useNavigate();

  const routeLng = params.lng && SUPPORTED_LNGS.includes(params.lng) ? params.lng : null;
  const detectorLng = SUPPORTED_LNGS.includes(i18n.language) ? i18n.language : 'en';
  const effectiveLng = routeLng || detectorLng;

  const itemActive = (code) =>
    code === routeLng || (routeLng == null && code === detectorLng);

  const selectLanguage = (code) => {
    if (!SUPPORTED_LNGS.includes(code)) return;
    const fromLang = effectiveLng;
    setVisitorLang(code);
    trackEvent('language_change', { from_lang: fromLang, to_lang: code });
    const dest = pathForLanguage(code, params);
    navigate(dest);
    void i18n.changeLanguage(code);
  };

  return (
    <Dropdown className="language-switcher" drop="down">
      <Dropdown.Toggle
        variant="outline-light"
        size="sm"
        id="language-switcher-toggle"
        className="language-switcher__toggle"
        aria-label={LANGUAGE_LABELS[effectiveLng] || LANGUAGE_LABELS.en}
      >
        <span className="language-switcher__current">{LANGUAGE_LABELS[effectiveLng] || LANGUAGE_LABELS.en}</span>
      </Dropdown.Toggle>

      <Dropdown.Menu alignRight className="language-switcher__menu shadow-sm">
        {SUPPORTED_LNGS.map((code) => (
          <Dropdown.Item
            key={code}
            as="button"
            type="button"
            active={itemActive(code)}
            className="language-switcher__item"
            lang={code === 'he' ? 'he' : code === 'zh' ? 'zh-Hans' : code === 'ja' ? 'ja' : code === 'ar' ? 'ar' : code}
            onClick={() => selectLanguage(code)}
          >
            {LANGUAGE_LABELS[code]}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}
